import type { ElementType, ReactNode } from "react";
import { useId, useState } from "react";

import { Separator } from "@core/components/Separator";
import { useExpandableContent } from "@core/hooks/useExpandableContent";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classNames from "classnames";

import { CardHeader } from "./components/CardHeader";

import styles from "./Card.module.scss";

export type CardVariant = "level-1" | "level-1-club" | "level-2" | "raised" | "raised-club";

type CardBaseProps = {
  variant?: CardVariant;
  title?: string;
  titleAs?: ElementType;
  description?: string;
  headerContent?: ReactNode;
  showSeparator?: boolean;
  children?: ReactNode;
} & Omit<SafeHtmlAttributes<HTMLDivElement>, "onToggle">;

type CardCollapsibleProps = CardBaseProps & {
  isCollapsible: true;
  isCollapsed?: boolean;
  defaultCollapsed?: boolean;
  onToggle?: (isCollapsed: boolean) => void;
  nonCollapsibleContent?: ReactNode;
  collapseButtonAriaLabel?: string;
};

type CardStaticProps = CardBaseProps & {
  isCollapsible?: false;
  isCollapsed?: never;
  defaultCollapsed?: never;
  onToggle?: never;
  nonCollapsibleContent?: never;
  collapseButtonAriaLabel?: never;
};

export type CardProps = CardCollapsibleProps | CardStaticProps;

export const Card = ({
  variant = "level-1",
  title,
  titleAs,
  description,
  headerContent,
  isCollapsible = false,
  isCollapsed: controlledCollapsed,
  defaultCollapsed = false,
  onToggle,
  nonCollapsibleContent,
  showSeparator = false,
  collapseButtonAriaLabel,
  children,
  ...rest
}: CardProps) => {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(defaultCollapsed);

  const isControlled = controlledCollapsed !== undefined;
  const isCollapsed = isControlled ? controlledCollapsed : uncontrolledCollapsed;

  const contentId = useId();
  const { innerRef, containerHeight, onTransitionEnd, updateHeight } = useExpandableContent({
    isExpanded: !isCollapsed,
  });

  const handleToggle = () => {
    const newCollapsed = !isCollapsed;

    if (!isControlled) {
      setUncontrolledCollapsed(newCollapsed);
    }

    if (!newCollapsed) {
      updateHeight();
    }

    onToggle?.(newCollapsed);
  };

  const shouldShowHeader = title || description || headerContent || isCollapsible;

  return (
    <div {...rest} className={classNames(styles.card, styles[variant])}>
      <div className={styles.headerContainer}>
        {shouldShowHeader && (
          <CardHeader
            title={title}
            titleAs={titleAs}
            description={description}
            headerContent={headerContent}
            isCollapsible={isCollapsible}
            isCollapsed={isCollapsed}
            contentId={contentId}
            onToggle={handleToggle}
            collapseButtonAriaLabel={collapseButtonAriaLabel}
          />
        )}

        {nonCollapsibleContent}
      </div>

      {isCollapsible ? (
        <div
          id={contentId}
          className={classNames(styles.collapsibleContainer, { [styles.expanded]: !isCollapsed })}
          style={{ maxHeight: containerHeight }}
          onTransitionEnd={onTransitionEnd}
        >
          <div className={classNames(styles.content, styles.contentSpacer)}>
            {showSeparator && (
              <div className={styles.separator}>
                <Separator />
              </div>
            )}
            <div className={styles.content} ref={innerRef}>
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className={classNames(styles.content, { [styles.contentSpacer]: shouldShowHeader })}>{children}</div>
      )}
    </div>
  );
};
